import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { NUMERIC_CONFIG } from '@/shared/config';
import { createWithdrawCryptoFormSchema } from './validation';
import type {
  AssetOptionWithBalance,
  NetworkOption,
  WithdrawAmountValues,
  WithdrawCryptoFormValues,
  WithdrawCryptoModalState,
} from './types';
import {
  useAddressCheckData,
  useCommissionData,
} from '@/features/withdraw-crypto';
import { useModalStore, useWithdrawOperationData } from '@/shared/lib';
import { useWithdrawTotalAmount } from '@/entities/operation';
import {
  checkCryptoAddress,
  useGetSavedCryptoAddresses,
} from '@/entities/crypto-address';

interface UseWithdrawCryptoFormOptions {
  onValidateSuccess: (data: WithdrawCryptoFormValues) => void;
}

export const useWithdrawCryptoForm = ({
  onValidateSuccess,
}: UseWithdrawCryptoFormOptions) => {
  const { t } = useTranslation();
  const { getState, setMultipleState } = useModalStore();
  const [selectedAsset, setSelectedAsset] = useState<
    AssetOptionWithBalance | undefined
  >();
  const [selectedNetwork, setSelectedNetwork] = useState<
    NetworkOption | undefined
  >();
  const [templateId, setTemplateId] = useState<string | undefined>();
  const [checkAddressResult, setCheckAddressResult] = useState('');
  const [invalidMessageResult, setInvalidMessageResult] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);

  const initFormData = getState('withdrawCryptoFormData') as
    | WithdrawCryptoFormValues
    | undefined;

  const {
    ewallet,
    depositWithdrawAssetsLoading,
    depositWithdrawAssetsError,
    depositWithdrawAssetsIsError,
    depositWithdrawAssetsData,
    assetsIconsData,
    assetsData,
    mainAccount,
  } = useWithdrawOperationData();

  const {
    data: savedCryptoAddresses,
    isLoading: isSavedCryptoAddressesLoading,
    error: savedCryptoAddressesError,
  } = useGetSavedCryptoAddresses();

  const assetOptions = useMemo<AssetOptionWithBalance[]>(() => {
    if (!assetsData || !assetsIconsData || !ewallet?.accounts) {
      return [];
    }

    if (!mainAccount) {
      return [];
    }
    const withdrawAssets =
      depositWithdrawAssetsData?.value?.withdraw?.crypto || [];
    const availableAssets = new Set<string>();

    withdrawAssets.forEach(network => {
      network.assets?.forEach(asset => {
        availableAssets.add(asset.code);
      });
    });

    return mainAccount.assets
      .filter(asset => availableAssets.has(asset.code) && asset.available > 0)
      .map(asset => {
        return {
          value: asset.code,
          code: asset.code,
          name: asset.name,
          baseAvailable: asset.baseAvailable,
          precision: asset.precision ?? NUMERIC_CONFIG.precision.default,
          icon: assetsIconsData.value?.[asset.code] || '',
          available: asset.available,
        };
      });
  }, [assetsData, assetsIconsData, ewallet, depositWithdrawAssetsData]);

  const assetNetworksMap = useMemo(() => {
    const map: Record<string, NetworkOption[]> = {};
    const networks = depositWithdrawAssetsData?.value?.withdraw?.crypto || [];

    networks.forEach(network => {
      network.assets?.forEach(asset => {
        if (!map[asset.code]) {
          map[asset.code] = [];
        }

        const existingNetwork = map[asset.code].find(
          n => n.code === network.networkCode
        );
        if (!existingNetwork) {
          map[asset.code].push({
            code: network.networkCode,
            name: network.networkName,
            assetCode: asset.code,
            minConfirm: network.minConfirm,
          });
        }
      });
    });

    return map;
  }, [depositWithdrawAssetsData]);

  const availableNetworks = useMemo(() => {
    return selectedAsset ? assetNetworksMap[selectedAsset.code] || [] : [];
  }, [selectedAsset, assetNetworksMap]);
  const minWithdraw = useMemo(() => {
    if (!selectedAsset || !selectedNetwork) return undefined;

    const network = depositWithdrawAssetsData?.value?.withdraw?.crypto?.find(
      n => n.networkCode === selectedNetwork.code
    );
    const asset = network?.assets?.find(a => a.code === selectedAsset.code);

    return asset?.minWithdraw;
  }, [selectedAsset, selectedNetwork, depositWithdrawAssetsData]);

  const maxWithdraw = useMemo(() => {
    if (!selectedAsset || !selectedNetwork) return undefined;

    const network = depositWithdrawAssetsData?.value?.withdraw?.crypto?.find(
      n => n.networkCode === selectedNetwork.code
    );
    const asset = network?.assets?.find(a => a.code === selectedAsset.code);

    return asset?.max;
  }, [selectedAsset, selectedNetwork, depositWithdrawAssetsData]);

  const validationSchema = useMemo(() => {
    return createWithdrawCryptoFormSchema(
      t,
      minWithdraw,
      selectedAsset?.available,
      checkAddressResult,
      totalAmount,
      invalidMessageResult,
      maxWithdraw,
      t('common.maxAmount', {
        amount: maxWithdraw,
        currency: selectedAsset?.code ?? '',
      })
    );
  }, [
    t,
    minWithdraw,
    maxWithdraw,
    selectedAsset?.available,
    checkAddressResult,
    totalAmount,
    selectedAsset?.code,
    selectedNetwork?.code,
  ]);

  const form = useForm<WithdrawCryptoFormValues>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    defaultValues: {
      assetCode: initFormData?.assetCode || '',
      networkName: initFormData?.networkName || '',
      cryptoAddress: initFormData?.cryptoAddress || '',
      amount: initFormData?.amount || '',
      includeFee: initFormData?.includeFee || false,
    },
  });

  const watchedValues = form.watch();

  useEffect(() => {
    if (!initFormData?.assetCode || !assetOptions.length || selectedAsset) {
      return;
    }

    const asset = assetOptions.find(a => a.code === initFormData.assetCode);
    if (asset) {
      setSelectedAsset(asset);
      form.setValue('assetCode', asset.code);
    }
  }, [initFormData, assetOptions, selectedAsset, form]);

  useEffect(() => {
    if (
      !selectedAsset ||
      !initFormData?.networkName ||
      !availableNetworks.length ||
      selectedNetwork
    ) {
      return;
    }

    const network = availableNetworks.find(
      n => n.code === initFormData.networkName
    );
    if (network) {
      setSelectedNetwork(network);
      form.setValue('networkName', network.code);
    }
  }, [selectedAsset, initFormData, availableNetworks, selectedNetwork, form]);

  const {
    checkAddressData,
    checkingAddressError,
    isValidatingAddress,
    checkAddressResult: addressCheckResult,
    invalidMessageResult: messageInvalidResult,
    manualAddressError,
    setManualAddressError,
    isAddressCheckFetching,
  } = useAddressCheckData({
    address: watchedValues.cryptoAddress,
    selectedNetwork,
    assetCode: watchedValues.assetCode,
    form,
  });

  useEffect(() => {
    setCheckAddressResult(addressCheckResult);
    setInvalidMessageResult(messageInvalidResult);
  }, [addressCheckResult, messageInvalidResult]);

  useEffect(() => {
    if (checkAddressResult) {
      form.trigger('cryptoAddress');
    }
  }, [checkAddressResult, form]);

  const { commissionData, commissionError, isGettingCommission } =
    useCommissionData({
      hasAmountError: !!form.formState.errors.amount,
      selectedAsset,
      selectedNetwork,
      amount: Number(watchedValues.amount),
      onTop: watchedValues.includeFee,
    });

  const [amount, includeFee] = form.watch(['amount', 'includeFee']);

  const amountValues: WithdrawAmountValues = {
    amount: String(amount),
    includeFee: includeFee ?? false,
  };

  const VALID_STATUSES = ['unknown', 'valid'];

  const { totalAmount: calculatedTotalAmount } = useWithdrawTotalAmount<
    WithdrawCryptoFormValues,
    WithdrawAmountValues
  >({
    form,
    watchedValues: amountValues,
    commissionData,
    assetCode: selectedAsset?.code,
    precision: selectedAsset?.precision,
    isCommissionLoading: isGettingCommission,
    additionalDependencies: [selectedAsset?.code],
    validationDependencies: [
      amountValues.amount,
      selectedNetwork?.code,
      totalAmount,
    ],
  });

  useEffect(() => {
    setTotalAmount(calculatedTotalAmount);
  }, [calculatedTotalAmount]);

  useEffect(() => {
    if (!selectedAsset) {
      setSelectedNetwork(undefined);
      form.setValue('networkName', '');
      return;
    }

    const preselectedNetwork =
      availableNetworks.length === 1 ? availableNetworks[0] : undefined;

    if (
      preselectedNetwork &&
      preselectedNetwork.assetCode === selectedAsset.code &&
      !selectedNetwork
    ) {
      setSelectedNetwork(preselectedNetwork);
      form.setValue('networkName', preselectedNetwork.name);
    }
  }, [availableNetworks, selectedAsset?.code, selectedNetwork, form]);

  useEffect(() => {
    const assetCode = watchedValues?.assetCode;
    const asset = assetOptions.find(a => a.code === assetCode);
    setSelectedAsset(asset);
    form.setValue('networkName', '');
  }, [watchedValues?.assetCode]);

  useEffect(() => {
    if (!selectedAsset?.code) {
      setSelectedNetwork(undefined);
      form.setValue('networkName', '');
      return;
    }

    if (availableNetworks.length === 1) {
      setSelectedNetwork(availableNetworks[0]);
      form.setValue('networkName', availableNetworks[0].code);
      return;
    }

    const network = availableNetworks.find(
      n => n.code === watchedValues.networkName
    );

    setSelectedNetwork(network);
    form.setValue('networkName', network?.code || '');
  }, [watchedValues?.networkName]);

  const handleMaxClick = () => {
    if (selectedAsset) {
      form.setValue('amount', String(selectedAsset.available));
    }
  };

  const handleFormSubmit = async (data: WithdrawCryptoFormValues) => {
    if (!selectedNetwork?.name) {
      form.setError('networkName', {
        type: 'manual',
        message: t('common.required'),
      });
      return;
    }

    try {
      const response = await checkCryptoAddress(
        selectedNetwork.name,
        data.cryptoAddress
      );

      if (VALID_STATUSES.includes(response.result)) {
        setMultipleState<WithdrawCryptoModalState>({
          withdrawCryptoFormData: data,
          selectedAsset: selectedAsset,
          selectedNetwork: selectedNetwork,
          accountNumber: mainAccount?.accNum,
          totalAmount: totalAmount,
          isNeedToSaveTemplate: !templateId,
        });
        onValidateSuccess(data);
      } else {
        form.setError('cryptoAddress', {
          type: 'manual',
          message: t('withdrawMethods.crypto.validation.checkAddressResult'),
        });
      }
    } catch (error) {
      setManualAddressError(error);
    }
  };

  return {
    form,
    watchedValues,
    assetOptions,
    availableNetworks,
    savedCryptoAddresses,
    selectedAsset,
    selectedNetwork,
    minWithdraw,
    hasWithdrawFee: commissionData ? commissionData.feeAmount > 0 : false,
    setTemplateId,
    isFromDataLoading: depositWithdrawAssetsLoading,
    isFormDataError: depositWithdrawAssetsIsError,
    formDataError: depositWithdrawAssetsError,
    error:
      checkingAddressError ||
      manualAddressError ||
      commissionError ||
      savedCryptoAddressesError,
    isLoading:
      isAddressCheckFetching ||
      isValidatingAddress ||
      isGettingCommission ||
      isSavedCryptoAddressesLoading,
    checkAddressData,
    commissionData,
    totalAmount,
    handleMaxClick,
    handleFormSubmit,
  };
};
